// =============================================================================
// giftcard.ts — staff calls to booking-giftcard (load a physical gift card on
// the WisePOS) and the ChargeDriver that plugs it into TerminalChargePanel.
// =============================================================================
import { call, type ChargeDriver, type ChargeStart, type ChargeStatus } from './terminal';

export interface GiftCardLoadStart extends ChargeStart {
  started: true;
  load_id: string;
  payment_intent_id: string;
  is_activation: boolean;
}

export interface GiftCardLoadStatus {
  load_status: 'pending' | 'succeeded' | 'failed' | 'canceled' | 'refunded';
  pi_status: string | null;
  reader_action_status: 'in_progress' | 'succeeded' | 'failed' | null;
  reader_action_failure_code: string | null;
  reader_action_failure_message: string | null;
  amount_due_cents: number;
  is_activation: boolean;
  balance_cents: number | null;
  expires_at: string | null;
  display_status: string | null;
}

const FN = 'booking-giftcard';

export function giftCardLoadStart(code: string, amountCents: number, locationId: string) {
  return call<GiftCardLoadStart>(
    { action: 'load_start', code, amount_cents: amountCents, location_id: locationId },
    FN,
  );
}

export function giftCardLoadStatus(loadId: string) {
  return call<GiftCardLoadStatus>({ action: 'load_status', load_id: loadId }, FN);
}

export function giftCardLoadRetry(loadId: string) {
  return call<{ processing: boolean }>({ action: 'load_retry', load_id: loadId }, FN);
}

export function giftCardLoadCancel(loadId: string) {
  return call<{ canceled: boolean; reason?: string }>({ action: 'load_cancel', load_id: loadId }, FN);
}

/**
 * A ChargeDriver for one load. `start` creates (or, after a busy reader,
 * re-pushes) the load; the driver remembers its id for the polls. Settled =
 * the load is recorded as succeeded, or Stripe already says succeeded (the
 * status call then records it).
 */
export function giftCardLoadDriver(
  code: string,
  amountCents: number,
  locationId: string,
  onLoadId?: (loadId: string) => void,
): ChargeDriver {
  let loadId: string | null = null;
  const noLoad = { ok: false, data: null, error: { error: 'pi_canceled' }, status: 409 } as const;
  return {
    start: async () => {
      const res = await giftCardLoadStart(code, amountCents, locationId);
      if (res.ok && res.data) {
        loadId = res.data.load_id;
        onLoadId?.(loadId);
      }
      return res;
    },
    status: async () => {
      if (!loadId) return { ...noLoad };
      const res = await giftCardLoadStatus(loadId);
      if (!res.ok || !res.data) return { ...res, data: null };
      const s = res.data;
      const data: ChargeStatus = {
        settled: s.load_status === 'succeeded' || s.pi_status === 'succeeded',
        pi_status: s.pi_status,
        reader_action_status: s.reader_action_status,
        reader_action_failure_code: s.reader_action_failure_code,
        reader_action_failure_message: s.reader_action_failure_message,
        amount_due_cents: s.amount_due_cents,
        checked_in: null,
      };
      return { ...res, data };
    },
    retry: async () => (loadId ? giftCardLoadRetry(loadId) : { ...noLoad }),
    cancel: async () =>
      loadId
        ? giftCardLoadCancel(loadId)
        : { ok: true, data: { canceled: true }, error: null, status: 200 },
  };
}
